import { Box, Grid, Typography, TextField, Button, Paper, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ConfigEntryType, ConfigType } from 'config/config';
import useUserData from 'hooks/useUserData';
import { useState, KeyboardEvent } from 'react';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { DndContext } from '@dnd-kit/core';
import { useConfigQuery, ConfigAPI, CONFIGS } from '@buildwise/libs/configurations';
import RenderAdminConfigEntryItem from './RenderAdminConfigEntryItem';
import { singular } from 'pluralize';

type RetroHeadingsProps = {
  isCompanyConfig: boolean;
};

function RetroHeadings({ isCompanyConfig }: RetroHeadingsProps) {
  const { isNovaAdmin, companyId } = useUserData();
  const [value, setValue] = useState('');
  const { updateConfigCache, config } = useConfigQuery({
    ...(isCompanyConfig && {
      companyId,
    }),
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });
  const [isAdding, setAdding] = useState(false);

  const onAddConfigEntry = async () => {
    try {
      setAdding(true);
      const { data } = await ConfigAPI.updateConfig({
        ...(config || {
          configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
          description: 'Phases',
        }),
        ...(isCompanyConfig && {
          companyId,
        }),
        entries: [
          ...(config?.entries || []),
          {
            entryCode: value.trim(),
            entryDescription: value.trim(),
            isActive: true,
            entryValues: [],
          },
        ],
      } as ConfigType);
      toast.success(`${value.trim()} added successfully`);
      updateConfigCache({
        updatedConfig: {
          config: data.config,
        },
      });
      setValue('');
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      setAdding(false);
    }
  };

  const updateConfigEntriesOrder = async (entries: ConfigEntryType[]) => {
    try {
      updateConfigCache({
        updatedConfig: {
          config: { ...config, entries } as ConfigType,
        },
      });
      if (config) {
        const { data } = await ConfigAPI.updateConfig({
          ...config,
          ...(isCompanyConfig && {
            companyId,
          }),
          sortFlag: false,
          entries,
        });
        updateConfigCache({
          updatedConfig: {
            config: data.config,
          },
        });
      }
    } catch (e) {
      updateConfigCache({
        updatedConfig: {
          config,
        },
      });
      toast.error(catchErrorMessage(e));
    }
  };

  const handleEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      onAddConfigEntry();
    }
  };

  return (
    <Container id={config?.configName}>
      <Box sx={{ padding: 4 }}>
        <Grid container>
          <Grid item md={3} xs={2} sx={{ paddingRight: 4 }}>
            <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
              {config?.description || 'Phases'}
            </Typography>
          </Grid>
          <Grid container item md={9} xs={12}>
            {isNovaAdmin && (
              <Grid container item md={12} sx={{ marginBottom: 2 }}>
                <Grid item md={10}>
                  <TextField
                    placeholder={`Enter ${singular(config?.description || 'phase').toLowerCase()}...`}
                    value={value}
                    onKeyUp={handleEnter}
                    onChange={(event) => setValue(event?.target.value)}
                    type="text"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item md={2}>
                  <Button
                    sx={{ ml: 2 }}
                    variant="outlined"
                    disabled={!value || isAdding}
                    onClick={onAddConfigEntry}
                  >
                    {isAdding ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : (
                      'Add'
                    )}
                  </Button>
                </Grid>
              </Grid>
            )}
            <Grid container item md={12}>
              <DndContext
                onDragEnd={({ active, over }) => {
                  if (over && active.id !== over?.id) {
                    const activeIndex = (config?.entries || []).findIndex(
                      ({ id }) => id === active.id,
                    );
                    const overIndex = (config?.entries || []).findIndex(({ id }) => id === over.id);

                    updateConfigEntriesOrder(
                      arrayMove(config?.entries || [], activeIndex, overIndex),
                    );
                  }
                }}
              >
                <SortableContext
                  items={(config?.entries || []).map((entry) => ({ id: entry?.id || '' }))}
                >
                  {config?.entries.map((entry, index) => (
                    <RenderAdminConfigEntryItem
                      key={entry.id}
                      config={config}
                      entry={entry}
                      index={index}
                      isCompanyConfig={isCompanyConfig}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

const Container = styled(Paper, { label: 'Container' })(({ theme }) => ({
  background: theme.palette.custom.white,
  borderRadius: 16,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  marginBottom: 24,
  overflow: 'hidden',
}));

export default RetroHeadings;
